import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useEmailContext } from '../../../contexts/EmailContext';
import { useAppContext } from '../../../AppContext';
import { Page } from '../../../models/Page';
import { fetchContact } from '../../../utils/fetchContact';
import { CassieWebsocketInvokeMethod } from '../../CassieContainer/websocket/CassieWebsocketContext';
import {
  IntegrationChannel,
  LookupEntityFieldType,
  NotifyConversationOpenedPayload,
  ThirdPartyWSMethod
} from '../../CassieContainer/websocket/types';
import { datadogLogs } from '@datadog/browser-logs';

interface UseEmailNotificationHandlerProps {
  invoke: CassieWebsocketInvokeMethod;
}

export function useEmailNotificationHandler({ invoke }: UseEmailNotificationHandlerProps): void {
  const { selectedEmail } = useEmailContext();
  const { activePage } = useAppContext();
  const invokeRef = useRef(invoke);
  const lastNotifiedEmailRef = useRef<string | null>(null);

  useEffect(() => {
    invokeRef.current = invoke;
  }, [invoke]);

  useEffect(() => {
    if (!selectedEmail || activePage !== Page.EMAIL) {
      return;
    }

    if (lastNotifiedEmailRef.current === selectedEmail) {
      return;
    }

    const contact = fetchContact(selectedEmail);
    if (!contact) return;

    const threadConversationId = contact.getContactAssociationId() ?? selectedEmail;

    let customerEmail: string | undefined;
    try {
      customerEmail = (contact.getCustomerEndpoint() as { emailAddress?: string })?.emailAddress;
    } catch {
      customerEmail = undefined;
    }

    let queueName: string | undefined;
    try {
      queueName = contact.getQueue().name;
    } catch {
      queueName = undefined;
    }

    const customerId = contact.getAttributes().customer_id?.value;
    const intentInteractionId = contact.getAttributes().intent_interaction_id?.value;

    const payload: Partial<NotifyConversationOpenedPayload> = {
      channel: IntegrationChannel.CATO,
      channelConversationId: threadConversationId,
      conversationSubsidiaryId: queueName ? parseInt(queueName) : undefined,
      intentInteractionId,
      ...(customerId && {
        lookupCustomer: {
          fieldType: LookupEntityFieldType.CUSTOMER_ID,
          fieldValue: customerId
        }
      })
    };

    if (customerEmail) {
      payload.lookupCustomer = {
        fieldType: LookupEntityFieldType.EMAIL,
        fieldValue: customerEmail
      };
      payload.visualIdentifier = customerEmail;
    }

    void invokeRef.current(
      ThirdPartyWSMethod.NOTIFY_CONVERSATION_OPENED,
      payload as NotifyConversationOpenedPayload
    );
    datadogLogs.logger.info('WEBSOCKET event sent to Cassie', {
      event: ThirdPartyWSMethod.NOTIFY_CONVERSATION_OPENED,
      channel: 'email'
    });

    lastNotifiedEmailRef.current = selectedEmail;
  }, [selectedEmail, activePage]);

  useEffect(() => {
    if (selectedEmail) {
      return;
    }

    if (!lastNotifiedEmailRef.current) {
      return;
    }

    void invokeRef.current(ThirdPartyWSMethod.NOTIFY_CONVERSATION_CLOSED, {
      channel: IntegrationChannel.CATO
    });
    datadogLogs.logger.info('WEBSOCKET event sent to Cassie', {
      event: ThirdPartyWSMethod.NOTIFY_CONVERSATION_CLOSED,
      channel: 'email'
    });

    lastNotifiedEmailRef.current = null;
  }, [selectedEmail]);
}

